import type { Offer } from 'types/seller'

import { Fragment, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { formatDate, getElement, toCurrency, formatZipCode } from 'utils'
import { useStore } from 'store'
import { energyType } from 'common'
import copy from 'copy.json'
import { paths } from 'paths'

import { DownloadIcon, InfoIcon, PencilIcon, PlusIcon, PowerIcon } from 'icons'
import Button from 'components/Button'
import Status from 'components/Status'
import Spinner from 'components/Spinner'
import DeleteButton from 'components/DeleteButton'
import ButtonLink from 'components/ButtonLink'
import ConfirmationWarnModal from 'modals/ConfirmationWarnModal'
import DeleteResourceModal from 'modals/DeleteResourceModal'
import StatusHistoryModal from 'modals/StatusHistoryModal'
import GenerateStatisticDataModal from 'modals/GenerateStatisticDataModal'
import ConfirmationEditResourceModal from 'modals/ConfirmationEditResourceModal'
import UpdateProfileFileModal from 'modals/UpdateProfileFileModal'
import EditBuiltOfferDetailsModal from './EditBuiltOfferDetailsModal'
import EditPlannedOfferDetailsModal from './EditPlannedOfferDetailsModal'
import DetailsWrapper from 'components/layout/DetailsWrapper'
import Detail from 'components/layout/Detail'

const form_texts = copy.common.form
const texts = copy.seller.offer_details
const button = copy.common.button
const profile_file_text = copy.modal.profile_file

type Props = {
  offer: Offer
}

export default function OfferDetailsRow({ offer, ...props }: Props) {
  const { sellerId } = useParams()
  const { api, searchOffers } = useStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [windStatisticLoading, setWindStatisticLoading] = useState(false)
  const [expandDetails, setExpandDetails] = useState(false)
  const [offerDetails, setOfferDetails] = useState<Offer | null>(null)
  const [turnOffOfferModalOpen, setTurnOffOfferModalOpen] = useState(false)
  const [turnOffOfferReasonModalOpen, setTurnOffOfferReasonModalOpen] = useState(false)
  const [deleteOfferModalOpen, setDeleteOfferModalOpen] = useState(false)
  const [deleteOfferReasonModalOpen, setDeleteOfferReasonModalOpen] = useState(false)
  const [statusHistoryModalOpen, setStatusHistoryModalOpen] = useState(false)
  const [generateStatisticDataModalOpen, setGenerateStatisticDataModalOpen] = useState(false)
  const [editBuiltOfferDetailsModalOpen, setEditBuiltOfferDetailsModalOpen] = useState(false)
  const [confirmationEditModal, setConfirmationEditModal] = useState(false)
  const [editPlannedOfferDetailsModalOpen, setEditPlannedOfferDetailsModalOpen] = useState(false)
  const [updateProfileFileModalOpen, setUpdateProfileFileModalOpen] = useState(false)

  useEffect(() => {
    if (Number(location.state) === offer.id) {
      handleOfferDetailsClick(offer.id)
      getElement(offer.id.toString())?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [location.state, offer.id]) // eslint-disable-line

  const getOffersDetails = async (offerId: number) => {
    setLoading(true)

    const response = await api.getResource('offers', offerId)
    setOfferDetails(response)
    setLoading(false)
  }

  const handleOfferActivation = async (
    offerId: number,
    purpose: 'activate' | 'deactivate',
    reason?: string
  ) => {
    const actionReason = purpose === 'activate' ? 'Aktywacja oferty' : reason
    const success: boolean = await api.activationResource('offer', offerId, purpose, actionReason)

    if (!success) return false

    handleOfferDataRefresh(offerId)
    return true
  }

  const handleOfferDetailsClick = (offerId: number) => {
    if (!expandDetails) {
      getOffersDetails(offerId)
    }

    setExpandDetails(current => !current)
  }

  const handleRefreshData = async (offerId: number) => {
    if (expandDetails) {
      await getOffersDetails(offerId)
    }
  }

  const handleDownloadProfileFile = (
    profileFileType: 'original' | 'processed',
    demandId: number
  ) => {
    api.getProfileFiles('offers', profileFileType, demandId)
  }

  const handleOfferDelete = async (offerId: number, reason: string) => {
    const success = await api.deleteResource('offers', offerId, reason)

    if (!success) return false

    handleOfferDataRefresh(offerId)
    return true
  }

  const handleEditOfferClick = (buildingPermitNumber: string, hasProcessedProfileFile: boolean) => {
    if (hasProcessedProfileFile) {
      setConfirmationEditModal(true)
    } else {
      if (buildingPermitNumber) setEditPlannedOfferDetailsModalOpen(true)
      else setEditBuiltOfferDetailsModalOpen(true)
    }
  }

  const handleConfirmationEditClick = () => {
    setConfirmationEditModal(false)

    if (offerDetails?.building_permit_number) setEditPlannedOfferDetailsModalOpen(true)
    else setEditBuiltOfferDetailsModalOpen(true)
  }

  const handleUpdateProfileFile = async (
    demandId: number,
    profileFile: File | null,
    reason: string
  ) => {
    const requestData = { reason: reason }
    const formData = new FormData()
    formData.append(
      'changeReasonRequest',
      new Blob([JSON.stringify(requestData)], { type: 'application/json' })
    )
    if (profileFile) formData.append('profileFile', profileFile)
    const success = await api.putAdminProfileFile(formData, 'offer', demandId)

    if (!success) return false

    handleOfferDataRefresh(demandId)
    return true
  }

  const handleOfferDataRefresh = async (offerId: number) => {
    if (sellerId) {
      getOffersDetails(offerId)
      await searchOffers(parseInt(sellerId))
    }
  }

  const handleGenerateStatisticClick = async () => {
    if (offerDetails?.energy_type === 'SOLAR') setGenerateStatisticDataModalOpen(true)
    if (offerDetails?.energy_type === 'WIND') {
      setWindStatisticLoading(true)

      await api.assignStatisticalProfile(offerDetails.id, 'WIND')
      await handleRefreshData(offerDetails.id)

      setWindStatisticLoading(false)
    }
  }

  return (
    <Fragment {...props}>
      <tr
        className={expandDetails ? 'expandedRow firstExpandedRow' : undefined}
        data-testid={offer.id}
      >
        <td>
          <Status statusType={offer.status} />
        </td>

        <td>
          <strong>{offer.id}</strong>
        </td>

        <td>
          <strong>{energyType[offer.energy_type]}</strong>
        </td>

        <td>
          <strong>{offer.osd_name}</strong>
        </td>

        <td>{offer.electric_energy_meter_number}</td>
        <td>
          {offer.installation_power.toFixed(2).replace('.00', '')} {copy.common.MW}
        </td>
        <td>
          {offer.yearly_power_production
            ? offer.yearly_power_production + ' ' + copy.common.MWh
            : '-'}
        </td>
        <td>{toCurrency(offer.energy_price)}</td>

        <td>
          <Button
            secondary={expandDetails}
            small
            style={{ minWidth: 85, marginLeft: 'auto' }}
            onClick={() => handleOfferDetailsClick(offer.id)}
          >
            {loading ? <Spinner size={16} /> : expandDetails ? button.roll : button.unroll}
          </Button>
        </td>
      </tr>

      {offerDetails && (
        <>
          <tr className={expandDetails ? 'expandedRow' : 'hiddenRow'}>
            <td colSpan={9}>
              <DetailsWrapper>
                <DetailsWrapper>
                  <Detail>
                    <span>{form_texts.installation_location}</span>
                    <span>{`${formatZipCode(offerDetails.location_zip_code)} ${
                      offerDetails.location_city
                    }`}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.energy_point_number}</span>
                    <span>{offerDetails.energy_point_number || '-'}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.building_permit_number}</span>
                    <span>{offerDetails.building_permit_number || '-'}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.energy_multiplier}</span>
                    <span>{`x${offerDetails.energy_multiplier}`}</span>
                  </Detail>
                </DetailsWrapper>

                <DetailsWrapper>
                  <Detail>
                    <span>{form_texts.production_start_date}</span>
                    <span>
                      {formatDate(offerDetails.production_start_date, {
                        options: {
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric'
                        }
                      })}
                    </span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.contract_length}</span>
                    <span>
                      {copy.common.from} {offerDetails.minimal_contract_length}{' '}
                      {copy.common.months_abbr} {copy.common.to}{' '}
                      {offerDetails.maximal_contract_length} {copy.common.months_abbr}
                    </span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.created_date}</span>
                    <span>{formatDate(offerDetails.created_date)}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.last_modified_date}</span>
                    <span>{formatDate(offerDetails.last_modified_date)}</span>
                  </Detail>
                </DetailsWrapper>
              </DetailsWrapper>

              <DetailsWrapper style={{ marginTop: 24 }}>
                {(offerDetails.has_original_profile_file ||
                  offerDetails.has_processed_profile_file ||
                  !offerDetails.has_energy_profile) && (
                  <Detail>
                    <h3>
                      {offerDetails.has_original_profile_file
                        ? texts.subheading_profile_file
                        : texts.subheading_profile}
                    </h3>

                    {!offerDetails.has_energy_profile &&
                      !offerDetails.has_original_profile_file && (
                        <DownloadWrapper>
                          <Detail>
                            <span>{texts.original_profile_file}</span>
                            <Button small onClick={() => setUpdateProfileFileModalOpen(true)}>
                              <PlusIcon />
                              {button.add}
                            </Button>
                          </Detail>

                          <Detail>
                            <span>{texts.generate}</span>
                            <Button
                              small
                              onClick={handleGenerateStatisticClick}
                              style={{ minWidth: 274, minHeight: 38 }}
                            >
                              {windStatisticLoading ? <Spinner size={16} /> : texts.generate_button}
                            </Button>
                          </Detail>
                        </DownloadWrapper>
                      )}

                    {offerDetails.has_original_profile_file && (
                      <DownloadWrapper>
                        <Detail>
                          <span>{texts.original_profile_file}</span>
                          <ProfileFileDownloadChangeWrapper>
                            <Button
                              secondary
                              small
                              disabled={!offerDetails.has_original_profile_file}
                              onClick={() => {
                                handleDownloadProfileFile('original', offerDetails.id)
                              }}
                            >
                              <DownloadIcon />
                              {button.download}
                            </Button>
                            <ButtonLink onClick={() => setUpdateProfileFileModalOpen(true)}>
                              {profile_file_text.change_file}
                            </ButtonLink>
                          </ProfileFileDownloadChangeWrapper>
                        </Detail>

                        <Detail>
                          <span>{texts.processed_profile_file}</span>
                          {!offerDetails.has_processed_profile_file &&
                            offerDetails.status !== 'DELETED' && (
                              <Button
                                small
                                onClick={() =>
                                  navigate(
                                    `${paths.file_upload}/${offerDetails.id}/${paths.format_choice}`
                                  )
                                }
                              >
                                <PlusIcon />
                                {button.add}
                              </Button>
                            )}

                          {offerDetails.has_processed_profile_file && (
                            <Button
                              secondary
                              small
                              onClick={() =>
                                handleDownloadProfileFile('processed', offerDetails.id)
                              }
                            >
                              <DownloadIcon />
                              {button.download}
                            </Button>
                          )}
                        </Detail>
                      </DownloadWrapper>
                    )}
                  </Detail>
                )}

                <Detail>
                  <h3>{texts.subheading_status}</h3>
                  <Statuses>
                    <Status statusType={offerDetails.status} />

                    {offerDetails?.status_history?.length > 0 && (
                      <ButtonLink onClick={() => setStatusHistoryModalOpen(true)}>
                        {texts.status_history}
                      </ButtonLink>
                    )}
                  </Statuses>

                  {offerDetails.has_energy_profile && offerDetails.status === 'INACTIVE' && (
                    <Button
                      small
                      style={{ maxWidth: 126 }}
                      onClick={() => handleOfferActivation(offerDetails.id, 'activate')}
                    >
                      <PowerIcon />
                      {button.activate}
                    </Button>
                  )}

                  {offerDetails.has_energy_profile &&
                    (offerDetails.status === 'ACTIVE' ||
                      offerDetails.status === 'RESERVED' ||
                      offerDetails.status === 'ACCEPTED') && (
                      <Button
                        small
                        secondary
                        warning
                        style={{ maxWidth: 126 }}
                        onClick={() => setTurnOffOfferModalOpen(true)}
                      >
                        <PowerIcon />
                        {button.turn_off}
                      </Button>
                    )}

                  {!offerDetails.has_energy_profile &&
                    offerDetails.has_original_profile_file &&
                    offerDetails.status !== 'DELETED' && (
                      <OfferInfo>
                        <InfoIcon /> <span>{texts.info}</span>
                      </OfferInfo>
                    )}
                </Detail>

                <ActionButtons>
                  {offerDetails.status === 'INACTIVE' && (
                    <Detail style={{ marginLeft: 'auto' }}>
                      <Button
                        small
                        secondary
                        style={{ marginTop: 'auto', height: 38 }}
                        onClick={() =>
                          handleEditOfferClick(
                            offerDetails.building_permit_number,
                            offerDetails.has_processed_profile_file
                          )
                        }
                      >
                        <PencilIcon />
                        {button.edit}
                      </Button>
                    </Detail>
                  )}

                  {offerDetails.status !== 'DELETED' && offerDetails.status !== 'CLOSED' && (
                    <Detail style={{ marginLeft: 'auto' }}>
                      <DeleteButton
                        onClick={() => setDeleteOfferModalOpen(true)}
                        style={{ marginTop: 'auto' }}
                      >
                        {button.delete}
                      </DeleteButton>
                    </Detail>
                  )}
                </ActionButtons>
              </DetailsWrapper>
            </td>
          </tr>

          <GenerateStatisticDataModal
            isModalOpen={generateStatisticDataModalOpen}
            onModalClose={() => setGenerateStatisticDataModalOpen(false)}
            offerId={offerDetails.id}
            energyType={offerDetails.energy_type}
            refreshData={() => handleRefreshData(offerDetails.id)}
          />

          {/* Turn off offer */}
          <ConfirmationWarnModal
            title={texts.modal.turn_off.title}
            description={texts.modal.turn_off.description}
            isModalOpen={turnOffOfferModalOpen}
            onModalClose={() => setTurnOffOfferModalOpen(false)}
            onModalConfirmation={() => {
              setTurnOffOfferReasonModalOpen(true)
              setTurnOffOfferModalOpen(false)
            }}
            confirmButtonLabel={button.turn_off}
            closeButtonLabel={button.close}
          />

          <DeleteResourceModal
            title={texts.modal.turn_off_reason.title}
            status={offerDetails.status}
            resourceId={offerDetails.id}
            isModalOpen={turnOffOfferReasonModalOpen}
            onModalClose={() => setTurnOffOfferReasonModalOpen(false)}
            onModalConfirmation={(reason: string) =>
              handleOfferActivation(offerDetails.id, 'deactivate', reason)
            }
            confirmButtonLabel={button.turn_off}
            closeButtonLabel={button.cancel}
          />

          {/* Delete offer */}
          <ConfirmationWarnModal
            title={texts.modal.delete.title}
            description={texts.modal.delete.description}
            isModalOpen={deleteOfferModalOpen}
            onModalClose={() => setDeleteOfferModalOpen(false)}
            onModalConfirmation={() => {
              setDeleteOfferReasonModalOpen(true)
              setDeleteOfferModalOpen(false)
            }}
            confirmButtonLabel={button.delete}
            closeButtonLabel={button.close}
          />

          <DeleteResourceModal
            title={texts.modal.delete_reason.title}
            status={offerDetails.status}
            resourceId={offerDetails.id}
            isModalOpen={deleteOfferReasonModalOpen}
            onModalClose={() => setDeleteOfferReasonModalOpen(false)}
            onModalConfirmation={(reason: string) => handleOfferDelete(offerDetails.id, reason)}
            confirmButtonLabel={button.delete}
            closeButtonLabel={button.cancel}
          />

          {/* Edit build offer data */}
          <EditBuiltOfferDetailsModal
            offerData={offerDetails}
            isModalOpen={editBuiltOfferDetailsModalOpen}
            onModalClose={() => setEditBuiltOfferDetailsModalOpen(false)}
            refreshData={() => handleOfferDataRefresh(offerDetails.id)}
          />

          {/* Confirmation edit demand data */}
          <ConfirmationEditResourceModal
            clientType="seller"
            isModalOpen={confirmationEditModal}
            onModalClose={() => setConfirmationEditModal(false)}
            onModalConfirmation={handleConfirmationEditClick}
          />

          {/* Edit planned offer data */}
          <EditPlannedOfferDetailsModal
            offerData={offerDetails}
            isModalOpen={editPlannedOfferDetailsModalOpen}
            onModalClose={() => setEditPlannedOfferDetailsModalOpen(false)}
            refreshData={() => handleOfferDataRefresh(offerDetails.id)}
          />

          {/* Status history */}
          <StatusHistoryModal
            statusHistory={offerDetails.status_history}
            isModalOpen={statusHistoryModalOpen}
            onModalClose={() => setStatusHistoryModalOpen(false)}
            closeButtonLabel={button.close}
          />

          {/* Update profile file modal */}
          <UpdateProfileFileModal
            title={
              offerDetails.has_original_profile_file
                ? offerDetails.status === 'ACTIVE'
                  ? profile_file_text.confirm_change_profile_file_deactivate_offer
                  : profile_file_text.confirm_change_profile_file
                : profile_file_text.confirm_add_profile_file
            }
            description={
              offerDetails.has_original_profile_file
                ? offerDetails.status === 'ACTIVE'
                  ? 'change_and_deactivate_offer'
                  : 'change_only'
                : ''
            }
            fieldErrorName="upsert_original_profile_file.profile_file"
            isModalOpen={updateProfileFileModalOpen}
            onModalClose={() => setUpdateProfileFileModalOpen(false)}
            onModalConfirmation={(file: File | null, reason: string) =>
              handleUpdateProfileFile(offerDetails.id, file, reason)
            }
          />
        </>
      )}
    </Fragment>
  )
}

const DownloadWrapper = styled.div`
  display: flex;
  gap: 30px;
`

const OfferInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font: var(--400-13);
`

const Statuses = styled.div`
  display: flex;
  gap: 24px;
`

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-left: auto;
`

const ProfileFileDownloadChangeWrapper = styled.div`
  display: flex;
  justify-content: center;
`
